import { ReactNode, useRef } from "react";
import {
  Checkbox,
  CircularProgress,
  MenuItem,
  Pagination,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Theme,
} from "@mui/material";
import { commonStyles } from "../commonStyles";
import { dashboardDensityClassNames } from "./DashboardDensityStyles";
import { DASHBOARD_SCROLL_SELECTOR, useNonModalSelect } from "./AnchoredMenu";

// Shared manage-page table (platform UI/UX polish, Phase A). Presentational
// only — sorting/filtering/pagination COMPUTATION stays in the pages. Every
// default is a verbatim copy of what ManageMembers renders today; the other
// manage pages adopt in Phase B by overriding the escape-hatch props.

export type SortDirection = "asc" | "desc";

export type DataTableColumn<Row, ColKey extends string = string> = {
  key: ColKey;
  // ReactNode (Phase B additive widening, B4): lets decorative header content
  // — e.g. ManageInvoices' uncontrolled checkbox column — ride as a regular
  // column with byte-identical cell geometry. Existing callers pass strings;
  // zero-runtime change.
  header: ReactNode;
  // default false → plain header text (no TableSortLabel)
  sortable?: boolean;
  // e.g. "18%" → header TableCell sx {width}
  width?: string;
  // merged after width; ManageMembers' non-sortable Actions column passes
  // { fontWeight: 800, width: "10%" }
  headerCellSx?: SxProps<Theme>;
  // page-owned cell markup (keeps each page's cells byte-identical)
  render: (row: Row) => ReactNode;
  // optional per-column body-cell sx (Invoices/Events Phase B)
  cellSx?: SxProps<Theme>;
  // Keep an existing actions column visible while the rest of a wide manage
  // table scrolls horizontally. Callers opt in explicitly so ordinary data
  // columns and non-manage tables retain their current behavior.
  stickyRight?: boolean;
  // Applied after the shared sticky styles; defaults remain unchanged.
  stickyRightSx?: SxProps<Theme>;
};

export type DataTableSort<ColKey extends string = string> = {
  column: ColKey;
  direction: SortDirection;
  // Toggle rules stay page-owned (Members: new column → asc; Emails: new
  // column → desc unless it is a text column).
  onRequestSort: (column: ColKey) => void;
};

// Phase B only (Events/Emails/Invoices leading checkbox column).
export type DataTableSelection<Row> = {
  isRowSelected: (row: Row) => boolean;
  onToggleRow: (row: Row) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  // indeterminate header checkbox (Events)
  someSelected?: boolean;
  disabled?: boolean;
  // Omitted styles use the shared green checkbox theme.
  checkboxSx?: SxProps<Theme>;
  headerAriaLabel?: string;
  getRowAriaLabel?: (row: Row) => string;
};

// Phase B only (Events); renders INSIDE the container, after </Table>.
export type DataTablePagination = {
  // 1-based
  page: number;
  pageCount: number;
  pageSize: number;
  // default [10, 25, 50]
  pageSizeOptions?: number[];
  // post-filter count
  totalCount: number;
  // "events" → `Showing ${a} to ${b} of ${n} events` / "Showing 0 events"
  itemNoun: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  paginationSx?: SxProps<Theme>;
  pageSizeSelectSx?: SxProps<Theme>;
};

export type DataTableProps<Row, ColKey extends string = string> = {
  ariaLabel: string;
  columns: ReadonlyArray<DataTableColumn<Row, ColKey>>;
  rows: ReadonlyArray<Row>;
  getRowKey: (row: Row) => string;
  sort?: DataTableSort<ColKey>;
  // CALLER-computed booleans. Members gates them on DIFFERENT datasets —
  // loading on the unfiltered cache (manageMembersLoading &&
  // allMembers.length === 0), empty on the filtered rows
  // (!manageMembersLoading && members.length === 0) — so DataTable must NOT
  // derive them from rows + a loading flag (that would change behavior when
  // a refetch runs while filters exclude every cached row).
  showLoadingRow?: boolean;
  loadingRowContent?: ReactNode;
  showEmptyRow?: boolean;
  emptyRowContent?: ReactNode;
  // Wide manage tables can keep feedback inside their horizontal scrollport.
  // Opt in so the existing feedback markup stays unchanged for other callers.
  keepFeedbackInViewport?: boolean;
  containerClassName?: string;
  tableSx?: SxProps<Theme>;
  headClassName?: string;
  rowClassName?: string | ((row: Row) => string);
  sortLabelSx?: SxProps<Theme>;
  // Phase B escape hatch: Events' arrow-stack sort button, Emails'
  // UnfoldMoreIcon TableSortLabel.
  renderSortLabel?: (args: {
    column: ColKey;
    // mirrors DataTableColumn.header's ReactNode widening
    label: ReactNode;
    active: boolean;
    direction: SortDirection;
    onRequestSort: (column: ColKey) => void;
  }) => ReactNode;
  // omit in Phase A
  selection?: DataTableSelection<Row>;
  // omit in Phase A
  pagination?: DataTablePagination;
  // A refetch is running while the previous rows stay on screen (IF10 G2,
  // audit T-3): the rows dim and ignore clicks, so the table keeps its
  // height and the page does not jump.
  refreshing?: boolean;
};

// Gap kept above the table when a page change scrolls to it.
const TABLE_SCROLL_MARGIN_PX = 16;

// Page and page-size changes bring the table top into view (audit T-5). The
// page scrolls only when the table top is above the visible area, and only
// to the table, not to the top of the page. Searches, sorts, filters and
// refetches never scroll.
export const scrollToTableTop = (table: HTMLElement | null) => {
  if (!table) return;
  const tableTop = table.getBoundingClientRect().top;
  const scroller = table.closest<HTMLElement>(DASHBOARD_SCROLL_SELECTOR);

  if (scroller) {
    const viewTop = scroller.getBoundingClientRect().top;
    if (tableTop >= viewTop) return;
    scroller.scrollTo({
      top: Math.max(0, scroller.scrollTop + tableTop - viewTop - TABLE_SCROLL_MARGIN_PX),
      left: scroller.scrollLeft,
      behavior: "auto",
    });
    return;
  }

  if (tableTop >= 0) return;
  window.scrollTo({
    top: Math.max(0, window.scrollY + tableTop - TABLE_SCROLL_MARGIN_PX),
    left: window.scrollX,
    behavior: "auto",
  });
};

// Verbatim ManageMembers table sx.
export const dataTableSx = {
  minWidth: 1120,
  tableLayout: "fixed",
  width: "100%",
  "& .MuiTableCell-root": {
    borderColor: "#E5E7EB",
    overflowWrap: "anywhere",
    px: 1.5,
    py: 2.5,
    verticalAlign: "middle",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    overflowWrap: "normal",
    whiteSpace: "nowrap",
  },
};

// Verbatim ManageMembers TableSortLabel sx.
export const sortLabelSx = {
  alignItems: "center",
  display: "inline-flex",
  fontWeight: 800,
  lineHeight: 1.2,
  maxWidth: "100%",
  whiteSpace: "nowrap",
  "& .MuiTableSortLabel-icon": {
    flexShrink: 0,
    marginLeft: 0.5,
  },
};

// Shared opaque chrome for fixed-right manage-table action columns. The
// background prevents scrolled data from showing through, while the divider
// and shadow make the frozen boundary clear without changing row content.
export const stickyActionColumnHeaderSx = {
  backgroundClip: "padding-box",
  backgroundColor: "#F9FAFB",
  borderLeft: "1px solid #E5E7EB",
  boxShadow: "-8px 0 12px -10px rgba(15, 23, 42, 0.35)",
  position: "sticky",
  right: 0,
  zIndex: 3,
} as const;

export const stickyActionColumnCellSx = {
  backgroundClip: "padding-box",
  backgroundColor: "#FFFFFF",
  borderLeft: "1px solid #E5E7EB",
  boxShadow: "-8px 0 12px -10px rgba(15, 23, 42, 0.35)",
  position: "sticky",
  right: 0,
  zIndex: 1,
} as const;

// Verbatim ManageEvents pagination sx (Phase B default).
export const dataTablePaginationSx = {
  "& .MuiPaginationItem-root": {
    border: "1px solid #E5E7EB",
    borderRadius: "8px",
    color: "#111827",
    fontWeight: 600,
  },
  "& .MuiPaginationItem-root.Mui-selected": {
    backgroundColor: "white",
    border: `1px solid ${commonStyles.cpaGold}`,
    color: commonStyles.cpaGold,
    "&:hover": { backgroundColor: "white" },
  },
  "& .MuiPaginationItem-ellipsis": {
    border: "none",
  },
};

// Verbatim ManageEvents page-size select sx (Phase B default).
export const dataTablePageSizeSelectSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: "8px",
    fontSize: 14,
    fontWeight: 600,
    "& fieldset": { borderColor: "#E5E7EB" },
  },
};

// Verbatim ManageMembers loading/empty rows (Phase A rule: shared defaults
// ARE ManageMembers' current values; other pages override in Phase B).
// Same loading row with a page's own noun, for example "Loading subscribers...".
export const dataTableLoadingRowContent = (text: string) => (
  <div className="flex items-center justify-center gap-3 py-8 text-sm font-semibold text-gray-500">
    <CircularProgress size={20} />
    {text}
  </div>
);

const defaultLoadingRowContent = dataTableLoadingRowContent("Loading members...");

const defaultEmptyRowContent = (
  <div className="py-8 text-center text-sm font-semibold text-gray-500">
    No members match the current search and filters.
  </div>
);

export const dataTableFeedbackContainerSx = {
  containerType: "inline-size",
} as const;

export const DataTableFeedback = ({ children }: { children: ReactNode }) => (
  <div className="sticky left-0 w-[calc(100cqw-32px)] max-w-full break-words">
    {children}
  </div>
);

const getHeaderCellSx = <Row, ColKey extends string>(
  column: DataTableColumn<Row, ColKey>
): SxProps<Theme> | undefined => {
  const styles: SxProps<Theme>[] = [];

  if (column.width) {
    styles.push({ width: column.width });
  }

  if (column.headerCellSx) {
    styles.push(
      ...(Array.isArray(column.headerCellSx)
        ? column.headerCellSx
        : [column.headerCellSx])
    );
  }

  if (column.stickyRight) {
    styles.push(stickyActionColumnHeaderSx);
    if (column.stickyRightSx) {
      styles.push(...(Array.isArray(column.stickyRightSx)
        ? column.stickyRightSx : [column.stickyRightSx]));
    }
  }

  return styles.length > 0 ? (styles as SxProps<Theme>) : undefined;
};

const getBodyCellSx = <Row, ColKey extends string>(
  column: DataTableColumn<Row, ColKey>
): SxProps<Theme> | undefined => {
  if (!column.stickyRight) {
    return column.cellSx;
  }

  const styles = column.cellSx
    ? Array.isArray(column.cellSx)
      ? column.cellSx
      : [column.cellSx]
    : [];

  const stickyOverrides = column.stickyRightSx
    ? Array.isArray(column.stickyRightSx)
      ? column.stickyRightSx
      : [column.stickyRightSx]
    : [];
  return [...styles, stickyActionColumnCellSx, ...stickyOverrides] as SxProps<Theme>;
};

export function DataTable<Row, ColKey extends string = string>({
  ariaLabel,
  columns,
  rows,
  getRowKey,
  sort,
  showLoadingRow = false,
  loadingRowContent = defaultLoadingRowContent,
  showEmptyRow = false,
  emptyRowContent = defaultEmptyRowContent,
  keepFeedbackInViewport = false,
  containerClassName = "mt-5 w-full overflow-x-auto rounded-lg border border-gray-300 bg-white",
  tableSx = dataTableSx,
  headClassName = "bg-gray-50",
  rowClassName = "hover:bg-gray-50",
  sortLabelSx: sortLabelSxProp = sortLabelSx,
  renderSortLabel,
  selection,
  pagination,
  refreshing = false,
}: DataTableProps<Row, ColKey>) {
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const pageSizeSelect = useNonModalSelect();
  const messageRowColSpan = columns.length + (selection ? 1 : 0);
  const pageSizeOptions = pagination?.pageSizeOptions ?? [10, 25, 50];
  const renderFeedback = (content: ReactNode) => keepFeedbackInViewport ? (
    <DataTableFeedback>{content}</DataTableFeedback>
  ) : content;

  return (
    <TableContainer
      ref={tableContainerRef}
      className={`${containerClassName} ${dashboardDensityClassNames.dataTable}`}
      sx={keepFeedbackInViewport ? dataTableFeedbackContainerSx : undefined}
    >
      <Table aria-label={ariaLabel} aria-busy={refreshing || undefined} sx={tableSx}>
        <TableHead className={headClassName}>
          <TableRow>
            {selection && (
              <TableCell padding="checkbox">
                <Checkbox
                  disabled={selection.disabled}
                  checked={selection.allSelected}
                  indeterminate={
                    !selection.allSelected && Boolean(selection.someSelected)
                  }
                  onChange={selection.onToggleAll}
                  inputProps={{ "aria-label": selection.headerAriaLabel }}
                  sx={selection.checkboxSx}
                />
              </TableCell>
            )}
            {columns.map((column) => (
              <TableCell
                key={column.key}
                sx={getHeaderCellSx(column)}
                sortDirection={
                  column.sortable && sort?.column === column.key
                    ? sort.direction
                    : false
                }
              >
                {column.sortable && sort ? (
                  renderSortLabel ? (
                    renderSortLabel({
                      column: column.key,
                      label: column.header,
                      active: sort.column === column.key,
                      direction: sort.direction,
                      onRequestSort: sort.onRequestSort,
                    })
                  ) : (
                    <TableSortLabel
                      active={sort.column === column.key}
                      direction={
                        sort.column === column.key ? sort.direction : "asc"
                      }
                      onClick={() => sort.onRequestSort(column.key)}
                      sx={sortLabelSxProp}
                    >
                      {column.header}
                    </TableSortLabel>
                  )
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody
          data-refreshing={refreshing ? "true" : undefined}
          sx={refreshing ? { opacity: 0.55, pointerEvents: "none", transition: "opacity 120ms" } : undefined}
        >
          {showLoadingRow && (
            <TableRow>
              <TableCell colSpan={messageRowColSpan}>
                {renderFeedback(loadingRowContent)}
              </TableCell>
            </TableRow>
          )}
          {showEmptyRow && (
            <TableRow>
              <TableCell colSpan={messageRowColSpan}>
                {renderFeedback(emptyRowContent)}
              </TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow
              key={getRowKey(row)}
              className={
                typeof rowClassName === "function"
                  ? rowClassName(row)
                  : rowClassName
              }
            >
              {selection && (
                <TableCell padding="checkbox">
                  <Checkbox
                    disabled={selection.disabled}
                    checked={selection.isRowSelected(row)}
                    onChange={() => selection.onToggleRow(row)}
                    inputProps={{
                      "aria-label": selection.getRowAriaLabel?.(row),
                    }}
                    sx={selection.checkboxSx}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell key={column.key} sx={getBodyCellSx(column)}>
                  {column.render(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {pagination && (
        <div className="flex flex-col items-center justify-between gap-3 border-t border-gray-200 px-5 py-4 sm:flex-row">
          <p className="text-sm text-gray-600">
            {pagination.totalCount === 0
              ? `Showing 0 ${pagination.itemNoun}`
              : `Showing ${
                  (pagination.page - 1) * pagination.pageSize + 1
                } to ${Math.min(
                  pagination.page * pagination.pageSize,
                  pagination.totalCount
                )} of ${pagination.totalCount} ${pagination.itemNoun}`}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <Pagination
              count={pagination.pageCount}
              page={pagination.page}
              onChange={(_, value) => {
                if (value === pagination.page) return;
                pagination.onPageChange(value);
                scrollToTableTop(tableContainerRef.current);
              }}
              shape="rounded"
              sx={pagination.paginationSx ?? dataTablePaginationSx}
            />
            <TextField
              select
              size="small"
              value={pagination.pageSize}
              onChange={({ target }) => {
                pagination.onPageSizeChange(Number(target.value));
                scrollToTableTop(tableContainerRef.current);
              }}
              sx={pagination.pageSizeSelectSx ?? dataTablePageSizeSelectSx}
              SelectProps={{
                ...pageSizeSelect.selectProps,
                renderValue: (selected) => `${selected} / page`,
              }}
              inputProps={{ "aria-label": `${pagination.itemNoun} per page` }}
            >
              {pageSizeOptions.map((size) => (
                <MenuItem key={size} value={size}>
                  {size} / page
                </MenuItem>
              ))}
            </TextField>
          </div>
        </div>
      )}
    </TableContainer>
  );
}
